import type { InfiniteData } from '@tanstack/react-query';
import type { DownloadListItem } from '../types';
import { queryClient, trpc } from '@/utils/trpc';

export type ListDownloadsPage = {
  items: DownloadListItem[];
  countUnread: number;
  totalCount: number;
};

/** Clears an item's unread state in the downloads list cache; decrements countUnread only if it was unread. */
export const markDownloadItemReadInCache = (documentId: string): void => {
  queryClient.setQueryData<InfiniteData<ListDownloadsPage>>(
    trpc.downloadsRouter.getDownloadItems.queryKey(),
    (oldData) => {
      if (!oldData) return oldData;

      let didClearUnread = false;

      const pages = oldData.pages.map((page, index) => {
        const items = page.items.map((item) => {
          if (item.id !== documentId || !item.isUnread) return item;
          didClearUnread = true;
          return { ...item, isUnread: false };
        });

        return {
          ...page,
          items,
          // countUnread lives on page 0; decrement here so we avoid a second pass.
          // Safe only because we discard `pages` below when nothing was cleared.
          ...(index === 0
            ? { countUnread: Math.max(0, page.countUnread - 1) }
            : {}),
        };
      });

      // No unread item cleared — keep the original reference (avoids a new pages tree).
      if (!didClearUnread) {
        return oldData;
      }

      return { ...oldData, pages };
    },
  );
};
